import React, { useState, useEffect, useCallback } from 'react';
import ModuleTemplate from '../../ModuleTemplate';
import { useInstructionAudio } from '../../lib/useInstructionAudio';
import './Mentalrotation.css';

const S3_BASE_URL = 'https://7cdc3d1b-1cdd-4f85-aaa4-fa5c412f8f13.selstorage.ru/vocabulary';

const OBJECTS = {
  arrow: { url: `${S3_BASE_URL}/arrow.png`, label: 'стрелка', pronoun: 'её' },
  key: { url: `${S3_BASE_URL}/key.png`, label: 'ключ', pronoun: 'его' },
  rocket: { url: `${S3_BASE_URL}/rocket.png`, label: 'ракета', pronoun: 'её' },
};

const OBJECTS_BY_AGE = {
  '4': ['arrow', 'key', 'rocket'],
  '5': ['arrow', 'key', 'rocket'],
  '6': ['arrow', 'key', 'rocket'],
  '7-9': ['arrow', 'key', 'rocket'],
  '10-12': ['arrow', 'key', 'rocket'],
  '13-15': ['arrow', 'key', 'rocket'],
  '16+': ['arrow', 'key', 'rocket'],
};

const QUARTER_ANGLES = [0, 90, 180, 270];

const EXERCISES = [
  { key: 'right', correctAngle: 90, instruction: (label, pronoun) => `Как будет выглядеть ${label}, если ${pronoun} повернуть вправо?` },
  { key: 'left', correctAngle: 270, instruction: (label, pronoun) => `Как будет выглядеть ${label}, если ${pronoun} повернуть влево?` },
];

const shuffleArray = (arr) => {
  const shuffled = [...arr];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const Mentalrotation = ({ ageGroup, onBack, onComplete }) => {
  const { play: playInstruction, stop: stopInstruction } = useInstructionAudio();
  const availableObjects = OBJECTS_BY_AGE[ageGroup] || OBJECTS_BY_AGE['4'];

  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [answerLog, setAnswerLog] = useState([]);
  const [selected, setSelected] = useState(null);
  const [shuffledOptions, setShuffledOptions] = useState([]);
  const [showResult, setShowResult] = useState(false);

  const currentExercise = EXERCISES[currentExerciseIndex];
  const totalExercises = EXERCISES.length;

  const questions = availableObjects;
  const currentObjectKey = questions[currentQuestionIndex];
  const currentObject = OBJECTS[currentObjectKey];
  const totalQuestionsInExercise = questions.length;
  const totalQuestionsAll = totalExercises * availableObjects.length;

  useEffect(() => {
    if (currentObject) {
      setShuffledOptions(shuffleArray(QUARTER_ANGLES));
    }
    setSelected(null);
  }, [currentExerciseIndex, currentQuestionIndex]);

  // Озвучка инструкции при каждом новом вопросе
  const speakInstruction = useCallback(() => {
    if (currentObject && currentExercise) {
      const text = currentExercise.instruction(currentObject.label, currentObject.pronoun);
      playInstruction(text);
    }
  }, [currentObject, currentExercise]);

  useEffect(() => {
    speakInstruction();
  }, [currentExerciseIndex, currentQuestionIndex, speakInstruction]);

  // Кнопка «Повторить инструкцию»
  const repeatInstruction = useCallback(() => {
    speakInstruction();
  }, [speakInstruction]);

  const handleSelect = (angle) => {
    if (selected !== null) return;
    stopInstruction();
    setSelected(angle);
    const isCorrect = angle === currentExercise.correctAngle;
    setAnswers(prev => [...prev, isCorrect]);

    const mirrorAngle = currentExercise.correctAngle === 90 ? 270 : 90;
    const isMirrorConfusion = !isCorrect && angle === mirrorAngle;

    setAnswerLog(prev => [...prev, {
      object: currentObject.label,
      direction: currentExercise.key,
      isCorrect,
      isMirrorConfusion,
    }]);

    setTimeout(() => {
      if (currentQuestionIndex < totalQuestionsInExercise - 1) {
        setCurrentQuestionIndex(currentQuestionIndex + 1);
      } else if (currentExerciseIndex < totalExercises - 1) {
        setCurrentExerciseIndex(currentExerciseIndex + 1);
        setCurrentQuestionIndex(0);
      } else {
        setShowResult(true);
      }
    }, 1000);
  };

  useEffect(() => {
    if (showResult) {
      const timer = setTimeout(() => {
        const correctCount = answers.filter(a => a === true).length;
        const score = totalQuestionsAll > 0 ? Math.round((correctCount / totalQuestionsAll) * 100) : 0;

        const mirrorConfusionCount = answerLog.filter(a => a.isMirrorConfusion).length;

        const byDirection = {};
        answerLog.forEach(a => {
          if (!byDirection[a.direction]) byDirection[a.direction] = { total: 0, correct: 0 };
          byDirection[a.direction].total += 1;
          if (a.isCorrect) byDirection[a.direction].correct += 1;
        });

        if (onComplete) {
          onComplete({
            testId: 'Mentalrotation',
            score,
            details: {
              total: totalQuestionsAll,
              correct: correctCount,
              mirrorConfusionCount,
              byDirection,
            },
          });
        } else {
          onBack();
        }
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [showResult]);

  if (showResult) {
    const correctCount = answers.filter(a => a === true).length;
    return (
      <ModuleTemplate 
        progress={answers} 
        totalQuestions={totalQuestionsAll} 
        forceStop={true}
        onComplete={onComplete || onBack}
      >
        <div className="result-content">
          <h2 className="result-title">🎉 Упражнение завершено!</h2>
          <div className="result-score">
            <span className="score-number">{correctCount}</span>
            <span className="score-total"> / {totalQuestionsAll}</span>
          </div>
        </div>
      </ModuleTemplate>
    );
  }

  if (!currentObject) {
    return (
      <ModuleTemplate 
        progress={answers} 
        totalQuestions={totalQuestionsAll} 
        forceStop={true}
        onComplete={onComplete || onBack}
      >
        <div className="loading-text">Загрузка...</div>
      </ModuleTemplate>
    );
  }

  return (
    <ModuleTemplate
      progress={answers}
      totalQuestions={totalQuestionsAll}
      forceStop={true}
      onComplete={onComplete || onBack}
      onRepeat={repeatInstruction}
      extraContent={currentExercise.instruction(currentObject.label, currentObject.pronoun)}
      disabled={selected !== null}
    >
      <div className="rotation-container">

        <div className="rotation-sample-block">
          <div className="rotation-sample-frame">
            <img src={currentObject.url} alt="" className="rotation-image" style={{ transform: 'rotate(0deg)' }} />
          </div>
        </div>

        <div className="rotation-options-row">
          {shuffledOptions.map((angle) => {
            const isSelected = selected === angle;
            const isCorrectAnswer = angle === currentExercise.correctAngle;
            const showCorrect = isSelected && isCorrectAnswer;
            const showWrong = isSelected && !isCorrectAnswer;
            return (
              <button
                key={angle}
                className={`rotation-option-btn ${showCorrect ? 'rotation-correct' : ''} ${showWrong ? 'rotation-wrong' : ''}`}
                onClick={() => handleSelect(angle)}
                disabled={selected !== null}
              >
                <img src={currentObject.url} alt="" className="rotation-image" style={{ transform: `rotate(${angle}deg)` }} />
              </button>
            );
          })}
        </div>

      </div>
    </ModuleTemplate>
  );
};

export default Mentalrotation;